import { useRef, useState } from 'react';
import { studioApi, useDeleteAct, useMarkActSigned, useSendAct, useStudioActs, useStudioClient, useStudioProjects, useUpdateClient, useVoidAct } from '@/api/studio';
import type { Act, Client, Project } from '@/api/types';
import { ConfirmButton } from '@/components/ConfirmDialog';
import { DownloadButton } from '@/components/DownloadButton';
import { Dialog, Status } from '@/components/ui';
import { useStudioTerms } from '@/lib/contour';
import { formatDate, formatMoney } from '@/lib/format';
import { isoInDays } from '@/lib/lines';
import { actStatus, signedViaLabel } from '@/lib/status';
import { ActEditorDialog } from './ActEditor';

/**
 * The studio's acts of a client (or of one project): drafts to review, sent acts waiting for the
 * signature, signed ones. Only where the contour has acts. On the client's card it also holds the
 * setting «Черновик акта после оплаты счёта» (client.acts_enabled).
 */
export function ClientActs({ client, projects, projectId }: { client: Client; projects: Project[]; /** Acts of this project only. */ projectId?: string }) {
  const { acts: withActs } = useStudioTerms();
  const actsQ = useStudioActs({ clientId: client.id, projectId });
  const [editing, setEditing] = useState<Act | 'new'>();
  const [marking, setMarking] = useState<Act>();
  if (!withActs) return null;
  const list = actsQ.data;

  return (
    <section aria-label="Акты">
      <div className="mb-2 flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="text-sm font-semibold">Акты</h2>
        <button className="btn-secondary h-8" onClick={() => setEditing('new')}>
          Выставить акт
        </button>
      </div>
      {!projectId && <AfterPaymentSetting client={client} />}
      {!list ? (
        <p className="text-sm text-muted">{actsQ.isError ? 'Акты не загрузились.' : 'Загружаем…'}</p>
      ) : list.length === 0 ? (
        <p className="text-sm text-muted">Актов не было. Составьте акт из этапов, задач и счетов или с нуля.</p>
      ) : (
        <ul className="divide-y divide-line border-y border-line">
          {list.map((a) => (
            <ActRow key={a.id} act={a} onEdit={() => setEditing(a)} onMark={() => setMarking(a)} />
          ))}
        </ul>
      )}
      {editing && <ActEditorDialog key={editing === 'new' ? 'new' : editing.id} clientId={client.id} projects={projects} act={editing === 'new' ? undefined : editing} projectId={projectId} open onClose={() => setEditing(undefined)} />}
      {marking && <MarkSignedDialog act={marking} onClose={() => setMarking(undefined)} />}
    </section>
  );
}

/** The old checkbox of the client card: with it on, a paid invoice leaves a DRAFT act for the studio to check and send. */
function AfterPaymentSetting({ client }: { client: Client }) {
  const update = useUpdateClient();
  return (
    <label className="mb-3 flex items-start gap-2 text-sm">
      <input type="checkbox" className="mt-0.5" checked={client.actsEnabled !== false} disabled={update.isPending} onChange={(e) => update.mutate({ id: client.id, patch: { actsEnabled: e.target.checked } })} />
      <span>
        Черновик акта после оплаты счёта
        <span className="block text-muted">С галочкой оплата счёта создаёт черновик акта: вы проверяете его и отправляете клиенту. Снимите, если клиент согласен, что оплата счёта — приёмка работ: акт не готовится и не попадает во «Внимание».</span>
      </span>
    </label>
  );
}

function ActRow({ act: a, onEdit, onMark }: { act: Act; onEdit: () => void; onMark: () => void }) {
  const send = useSendAct();
  const del = useDeleteAct();
  const voidAct = useVoidAct();
  const [asking, setAsking] = useState<'delete' | 'void'>();
  const s = actStatus[a.status];
  const error = (send.error ?? del.error ?? voidAct.error) as Error | null;
  const closes = a.invoiceNumbers?.length ? `Закрывает ${a.invoiceNumbers.length === 1 ? 'счёт' : 'счета'} № ${a.invoiceNumbers.join(', ')}` : 'Без счетов';
  return (
    <li className={a.status === 'void' ? 'py-3 text-muted' : 'py-3'}>
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
        <div className="min-w-0 flex-1">
          <div className="font-medium">
            Акт № {a.number} <span className="font-normal text-muted">от {formatDate(a.issuedAt)}</span>
          </div>
          <div className="text-sm text-muted">
            {[closes, a.projectTitle, a.periodFrom || a.periodTo ? `период ${[a.periodFrom, a.periodTo].filter(Boolean).map((d) => formatDate(d!)).join(' — ')}` : ''].filter(Boolean).join(' · ')}
          </div>
          {a.status === 'signed' && a.signedVia && <div className="text-xs text-muted">подписан {signedViaLabel[a.signedVia]}{a.signedAt ? `, ${formatDate(a.signedAt)}` : ''}</div>}
          {a.status === 'signed' && (a.signerName || a.signedName) && (
            <div className="text-xs text-muted">
              {a.signerName && <>подписал {a.signerName}</>}
              {a.signedName && <>{a.signerName ? ', ' : ''}имя при подписи «{a.signedName}»</>}
            </div>
          )}
          {a.status === 'signed' && a.signedHash && (
            <div className="break-all text-xs text-muted">
              отпечаток файла <span className="font-mono">{a.signedHash}</span>
            </div>
          )}
        </div>
        <span className="num text-sm">{formatMoney(a.total)}</span>
        <Status tone={s.tone}>{s.label}</Status>
      </div>
      <div className="mt-2 flex flex-wrap items-center gap-1">
        {a.status === 'draft' && (
          <>
            <button className="btn-ghost h-7 px-2 text-xs" onClick={onEdit}>
              Изменить
            </button>
            <ConfirmButton
              label="Отправить на подпись"
              className="btn-primary h-7 px-2 text-xs"
              disabled={send.isPending}
              title="Отправить акт на подпись?"
              confirmLabel="Отправить на подпись"
              onConfirm={() => send.mutate(a.id)}
            >
              Акт № {a.number} на {formatMoney(a.total)} уйдёт клиенту на подпись, он получит уведомление. {closes}.
            </ConfirmButton>
          </>
        )}
        {(a.status === 'draft' || a.documentId) && a.status !== 'void' && (
          <DownloadButton className="btn-ghost h-7 px-2 text-xs" load={() => studioApi.studioActPdf(a.id)} name={a.fileName} label={`PDF акта № ${a.number}`}>
            {a.status === 'draft' ? 'Предпросмотр PDF' : 'PDF'}
          </DownloadButton>
        )}
        {a.scanDocumentId && (
          <DownloadButton className="btn-ghost h-7 px-2 text-xs" load={() => studioApi.downloadDocument(a.scanDocumentId!)} name={`Скан акта № ${a.number}.pdf`} label={`Скан акта № ${a.number}`}>
            Скан
          </DownloadButton>
        )}
        {(a.status === 'draft' || a.status === 'to_sign') && (
          <button className="btn-ghost h-7 px-2 text-xs" onClick={onMark}>
            Подписан на бумаге / в ЭДО
          </button>
        )}
        {a.status === 'to_sign' && (
          <button className="btn-ghost h-7 px-2 text-xs text-overdue" onClick={() => setAsking('void')}>
            Отозвать
          </button>
        )}
        {a.status === 'draft' && (
          <button className="btn-ghost h-7 px-2 text-xs text-overdue" onClick={() => setAsking('delete')}>
            Удалить
          </button>
        )}
      </div>
      {error && (
        <p role="alert" className="mt-1 text-xs text-overdue">
          {error.message}
        </p>
      )}
      <Dialog open={!!asking} onClose={() => setAsking(undefined)} title={asking === 'delete' ? 'Удалить черновик акта?' : 'Отозвать акт?'}>
        <div className="space-y-4 p-5 text-sm">
          <p>
            {asking === 'delete'
              ? `Черновик акта № ${a.number} будет удалён. Клиент его не видел.`
              : `Акт № ${a.number} перестанет быть виден клиенту, счета освободятся для нового акта. Подписанный акт отозвать нельзя.`}
          </p>
          <div className="flex gap-2">
            <button className="btn-secondary flex-1" onClick={() => setAsking(undefined)}>
              Оставить
            </button>
            <button className="btn-primary flex-1" disabled={del.isPending || voidAct.isPending} onClick={() => (asking === 'delete' ? del.mutate(a.id, { onSuccess: () => setAsking(undefined) }) : voidAct.mutate(a.id, { onSuccess: () => setAsking(undefined) }))}>
              {asking === 'delete' ? 'Удалить' : 'Отозвать'}
            </button>
          </div>
        </div>
      </Dialog>
    </li>
  );
}

/** The signature was made outside the portal: on paper or in EDO, with its date and, if there is one, the scan. */
export function MarkSignedDialog({ act, onClose }: { act: Act; onClose: () => void }) {
  const mark = useMarkActSigned();
  const [via, setVia] = useState<'paper' | 'edo'>('paper');
  const [signedOn, setSignedOn] = useState(isoInDays(0));
  const file = useRef<HTMLInputElement>(null);
  return (
    <Dialog open onClose={onClose} title={`Акт № ${act.number}: подписан вне кабинета`}>
      <form
        className="space-y-4 p-5"
        onSubmit={(e) => {
          e.preventDefault();
          mark.mutate({ id: act.id, input: { via, signedOn, file: file.current?.files?.[0] } }, { onSuccess: onClose });
        }}
      >
        <fieldset className="flex gap-4 text-sm">
          <legend className="mb-1.5 text-muted">Как подписан</legend>
          <label className="flex items-center gap-2">
            <input type="radio" name="via" checked={via === 'paper'} onChange={() => setVia('paper')} /> На бумаге
          </label>
          <label className="flex items-center gap-2">
            <input type="radio" name="via" checked={via === 'edo'} onChange={() => setVia('edo')} /> В ЭДО
          </label>
        </fieldset>
        <label className="block text-sm">
          <span className="mb-1.5 block text-muted">Дата подписания</span>
          <input className="field" type="date" value={signedOn} max={isoInDays(0)} onChange={(e) => setSignedOn(e.target.value)} required />
        </label>
        <label className="block text-sm">
          <span className="mb-1.5 block text-muted">Скан (PDF или фото), если есть</span>
          <input ref={file} type="file" accept=".pdf,.png,.jpg,.jpeg,application/pdf,image/png,image/jpeg" className="block w-full text-sm" />
        </label>
        {act.status === 'draft' && <p className="text-sm text-muted">Акт ещё не отправлен: PDF будет сформирован и положен в документы клиента, клиент получит уведомление.</p>}
        {mark.error && (
          <p role="alert" className="text-sm text-overdue">
            {(mark.error as Error).message}
          </p>
        )}
        <div className="flex justify-end gap-2">
          <button type="button" className="btn-ghost" onClick={onClose}>
            Отмена
          </button>
          <button className="btn-primary" disabled={mark.isPending}>
            Отметить подписанным
          </button>
        </div>
      </form>
    </Dialog>
  );
}

/** The «Акты» tab of a project: the acts of that project, and «Выставить акт» starting in it. */
export function ProjectActs({ project }: { project: Project }) {
  const clientQ = useStudioClient(project.clientId);
  const projectsQ = useStudioProjects(project.clientId);
  if (!clientQ.data || !projectsQ.data) return <p className="text-sm text-muted">Загружаем…</p>;
  return <ClientActs client={clientQ.data} projects={projectsQ.data} projectId={project.id} />;
}
